import { useState } from "react"
import { Button, Dialog, DialogActions, DialogContent, DialogHeader, ToastLevel, useIntl, useToast } from "@metsooutotec/modes-react-components"
import { NavigationDeleteEntityOptions } from "components/utils/modal-helper"
import { useNavigate } from "react-router-dom"
import styles from "./generic-delete-modal.module.css"

interface GenericDeleteModalProps<T> {
    isOpen: boolean
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    toastMessage: string
    navigateOnDelete?: {
        path: string
        state?: NavigationDeleteEntityOptions
    }
    entity: T | undefined
    getEntityName: (entity: T) => string
    onDelete: (entity: T) => Promise<{ passed: boolean; message?: string }>
    i18nKeys: {
        header: string
        content: string
        cancel: string
        confirm: string
    }
}

const GenericDeleteModal = <T,>({
    isOpen,
    entity,
    setIsOpen,
    navigateOnDelete,
    getEntityName,
    onDelete,
    i18nKeys,
    toastMessage,
}: GenericDeleteModalProps<T>) => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { setMessage } = useToast()
    const [isLoading, setLoading] = useState(false)
    const [errorState, setErrorState] = useState({ isError: false, errorMessage: "" })

    if (!isOpen || !entity) return null

    const name = getEntityName(entity)

    const handleConfirm = async (): Promise<void> => {
        setLoading(true)
        setErrorState({ isError: false, errorMessage: "" })

        try {
            const result = await onDelete(entity)

            if (!result.passed) {
                setErrorState({ isError: true, errorMessage: result.message || formatMessage("error.unknown") })
                return
            }
            setMessage(toastMessage, ToastLevel.SUCCESS, 6000)
            setIsOpen(false)
            if (result.passed && navigateOnDelete) navigate(navigateOnDelete.path, { state: navigateOnDelete.state })
        } catch (error) {
            console.error("Delete failed:", error)
            setErrorState({
                isError: true,
                errorMessage: formatMessage("error.unexpected"),
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={isOpen} dimmer size='small'>
            <DialogHeader>{formatMessage(i18nKeys.header)}</DialogHeader>

            {errorState.isError && <p className={styles.errorMessage}>{errorState.errorMessage}</p>}

            <DialogContent data-cy='dialog-content'>{`${formatMessage(i18nKeys.content)} '${name}'.`}</DialogContent>

            <DialogActions>
                <Button disabled={isLoading} onClick={() => setIsOpen(false)} subtle>
                    {formatMessage(i18nKeys.cancel)}
                </Button>
                <Button loading={isLoading} disabled={isLoading} onClick={handleConfirm} primary>
                    {formatMessage(i18nKeys.confirm)}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default GenericDeleteModal
